import React, { useEffect, useState, useRef } from 'react';
import { useStateValue } from "../GlobalState/StateProvider";
import { useNavigate } from 'react-router-dom';
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Card } from "primereact/card";
import { TabView, TabPanel } from 'primereact/tabview';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { FilterMatchMode } from 'primereact/api';
import axios from "axios";

function MailOrderHoneybee() {
    const navigate = useNavigate();
    const [{ user }, dispatch] = useStateValue();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [filters, setFilters] = useState({
        orderId: '',
        caseId: '',
        email: '',
        firstName: '',
        lastName: ''
    });
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [tableFilters, setTableFilters] = useState({
        'order_id': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'case_id': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'first_name': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'current_status': { value: null, matchMode: FilterMatchMode.CONTAINS },
        'tracking_id': { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const [editDialog, setEditDialog] = useState(false);
    const [editingOrder, setEditingOrder] = useState(null);
    const [editForm, setEditForm] = useState({
        current_status: '',
        tracking_id: '',
        delivery_date: null
    });
    const toast = useRef(null);
    const [expandedRows, setExpandedRows] = useState([]);

    const tabConfigs = [
        { label: 'Sent', status: 'sent', icon: 'pi pi-check-circle' },
        { label: 'Due', status: 'due', icon: 'pi pi-calendar' },
        { label: 'Issue Orders', status: 'issue', icon: 'pi pi-exclamation-triangle' }
    ];

    // Updated status options for Honeybee
    const statusOptions = [
        { label: 'Created', value: 'Created' },
        { label: 'Order Sent', value: 'Order Sent' },
        { label: 'RX_RECEIVED', value: 'RX_RECEIVED' },
        { label: 'RX_UPDATED', value: 'RX_UPDATED' },
        { label: 'SHIPMENT_UPDATED', value: 'SHIPMENT_UPDATED' },
        { label: 'ORDER_EXCEPTION', value: 'ORDER_EXCEPTION' },
        { label: 'Failed', value: 'Failed' },
        { label: 'Delivered', value: 'Delivered' },
        { label: 'In Transit', value: 'In Transit' },
        { label: 'Returned', value: 'Returned' },
        { label: 'Cancelled', value: 'Cancelled' }
    ];

    useEffect(() => {
        loadMailOrders();
    }, [activeTab, currentMonth, currentYear]);

    // Add custom CSS to hide filter icons
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .p-column-filter-row .p-column-filter-menu-button {
                display: none !important;
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const loadMailOrders = async () => {
        try {
            setLoading(true);
            const config = tabConfigs[activeTab];
            
            // Set authorization header
            if (user.token !== '' && user.token !== null) {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + user.token;
            }
            
            // Calculate date range based on tab and selected month/year
            let startDate = '';
            let endDate = '';
            
            // Get first and last day of the selected month
            const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
            const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
            
            if (config.status === 'due') {
                // For due today, show orders due in the selected month
                startDate = firstDayOfMonth.toISOString().split('T')[0];
                endDate = lastDayOfMonth.toISOString().split('T')[0];
            } else if (config.status === 'sent') {
                // For sent orders, show orders sent in the selected month
                startDate = firstDayOfMonth.toISOString().split('T')[0];
                endDate = lastDayOfMonth.toISOString().split('T')[0];
            } else if (config.status === 'issue') {
                startDate = '';
                endDate = '';
            }

            const response = await axios.post('/api/ha/getMailOrdersHoneybee', {
                orderId: filters.orderId,
                caseId: filters.caseId,
                email: filters.email,
                firstName: filters.firstName,
                lastName: filters.lastName,
                startDate: startDate,
                endDate: endDate,
                status: config.status
            });

            if (response.data.status === 'OK') {
                setData(response.data.data || []);
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.data.errMsg || 'Failed to load mail orders',
                    life: 3000
                });
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.errMsg || 'Failed to load mail orders',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        loadMailOrders();
    };

    const handleClearSearch = () => {
        setFilters({
            orderId: '',
            caseId: '',
            email: '',
            firstName: '',
            lastName: ''
        });
        loadMailOrders();
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleMonthNavigation = (direction) => {
        if (direction === 'forward') {
            if (currentMonth === 11) {
                setCurrentMonth(0);
                setCurrentYear(currentYear + 1);
            } else {
                setCurrentMonth(currentMonth + 1);
            }
        } else {
            if (currentMonth === 0) {
                setCurrentMonth(11);
                setCurrentYear(currentYear - 1);
            } else {
                setCurrentMonth(currentMonth - 1);
            }
        }
    };

    const handleTabChange = (e) => {
        setActiveTab(e.index);
    };

    const caseIdBodyTemplate = (rowData) => {
        return (
            <a 
                href="#" 
                onClick={(e) => {
                    e.preventDefault();
                    navigate(`/CaseDetail/${rowData.case_id}`);
                }}
                className="text-blue-500 hover:text-blue-700 underline"
            >
                {rowData.case_id}
            </a>
        );
    };

    const statusBodyTemplate = (rowData) => {
        const statusColors = {
            'sent': 'bg-success',
            'due_today': 'bg-warning',
            'issue': 'bg-danger',
            'pending': 'bg-info',
            'RX_RECEIVED': 'bg-info',
            'RX_UPDATED': 'bg-warning',
            'SHIPMENT_UPDATED': 'bg-success',
            'ORDER_EXCEPTION': 'bg-danger',
            'Failed': 'bg-danger',
            'Created': 'bg-secondary',
            'Order Sent': 'bg-primary'
        };
        
        return (
            <span className={`badge ${statusColors[rowData.current_status] || 'bg-secondary'}`}>
                {rowData.current_status || 'Unknown'}
            </span>
        );
    };

    const dateBodyTemplate = (rowData) => {
        if (!rowData.delivery_date) return 'N/A';
        return new Date(rowData.delivery_date).toLocaleDateString();
    };

    const createdBodyTemplate = (rowData) => {
        if (!rowData.created) return 'N/A';
        return new Date(rowData.created).toLocaleDateString();
    };

    const updatedBodyTemplate = (rowData) => {
        if (!rowData.updated) return 'N/A';
        return new Date(rowData.updated).toLocaleDateString();
    };

    const actionBodyTemplate = (rowData) => {
        const isFormularyFailure = rowData.current_status === 'Failed' &&
            !rowData.sent &&
            (String(rowData.message || '').includes('42260') || String(rowData.message || '').includes('Invalid Item Product Selection'));
        return (
            <div className="flex gap-1">
                {activeTab === 1 && (
                    <Button
                        icon="pi pi-send"
                        className="p-button-rounded p-button-primary p-button-sm"
                        onClick={() => handleSend(rowData)}
                        tooltip="Send to Honeybee"
                        loading={rowData.sending}
                    />
                )}
                {isFormularyFailure && (
                    <Button
                        icon="pi pi-replay"
                        className="p-button-rounded p-button-warning p-button-sm"
                        onClick={() => handleCreateResendRefill(rowData.order_id)}
                        tooltip="Create refill so doctor can send a new Honeybee Rx"
                        loading={rowData.resending}
                    />
                )}
                <Button
                    icon="pi pi-pencil"
                    className="p-button-rounded p-button-success p-button-sm"
                    onClick={() => handleEdit(rowData)}
                    tooltip="Edit Order"
                />
            </div>
        );
    };

    const expandedRowTemplate = (data) => {
        const formatJson = (jsonString) => {
            if (!jsonString) return 'N/A';
            try {
                return JSON.stringify(JSON.parse(jsonString), null, 2);
            } catch (e) {
                return jsonString; // Return as-is if not valid JSON
            }
        };

        return (
            <div className="p-3">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <h5 className="mb-3">Patient Information</h5>
                        <div className="space-y-2">
                            <div><strong>First Name:</strong> {data.first_name || 'N/A'}</div>
                            <div><strong>Last Name:</strong> {data.last_name || 'N/A'}</div>
                            <div><strong>Member ID:</strong> {data.member_id || 'N/A'}</div>
                            <div><strong>Honeybee Patient ID:</strong> {data.honeybee_patient_id || 'N/A'}</div>
                            <div><strong>DOB:</strong> {data.dob || 'N/A'}</div>
                            <div><strong>Phone:</strong> {data.phone || 'N/A'}</div>
                            <div><strong>Remaining Refills:</strong> {data.remaining_refills || 'N/A'}</div>
                            <div><strong>Active:</strong> {data.active ? 'Yes' : 'No'}</div>
                        </div>
                        <br />
                        <h5 className="mb-3">Fulfillment Information</h5>
                        <div className="space-y-2">
                            <div><strong>Fulfillment ID:</strong> {data.fulfilment_id || 'N/A'}</div>
                            <div><strong>Fulfillment #:</strong> {data.fulfillment_no || 'N/A'}</div>
                            <div><strong>Prescription ID:</strong> {data.prescription_id || 'N/A'}</div>
                            <div><strong>Event ID:</strong> {data.event_id || 'N/A'}</div>
                            <div><strong>Is Delivered:</strong> {data.is_delivered ? 'Yes' : 'No'}</div>
                            <div><strong>Sent:</strong> {data.sent ? 'Yes' : 'No'}</div>
                            <div><strong>Message:</strong> {data.message || 'N/A'}</div>
                        </div>
                    </div>
                    <div>
                        <h5 className="mb-3">Shipping Information</h5>
                        <div className="space-y-2">
                            <div><strong>Carrier:</strong> {data.carrier || 'N/A'}</div>
                            <div><strong>Shipping Method:</strong> {data.shipping_method || 'N/A'}</div>
                            <div><strong>Tracking ID:</strong> {data.tracking_id || 'N/A'}</div>
                        </div>
                        <br />
                        <h5 className="mb-3">Address Information</h5>
                        <div className="space-y-2">
                            <div><strong>Street 1:</strong> {data.streetAddress || 'N/A'}</div>
                            <div><strong>Street 2:</strong> {data.streetAddress2 || 'N/A'}</div>
                            <div><strong>City:</strong> {data.city || 'N/A'}</div>
                            <div><strong>State:</strong> {data.state || 'N/A'}</div>
                            <div><strong>Zip:</strong> {data.zip || 'N/A'}</div>
                            <div><strong>Country:</strong> {data.country || 'N/A'}</div>
                        </div>
                    </div>
                    <div className="col-span-2">
                        <h5 className="mb-3">Event JSON Response</h5>
                        <div className="bg-gray-100 p-3 rounded border">
                            <pre className="text-sm whitespace-pre-wrap overflow-x-auto">
                                {formatJson(data.json_response)}
                            </pre>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const handleSend = async (order) => {
        try {
            // Set loading state for this specific row
            setData(prevData => prevData.map(item => 
                item.order_id === order.order_id 
                    ? { ...item, sending: true }
                    : item
            ));

            // Set authorization header
            if (user.token !== '' && user.token !== null) {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + user.token;
            }

            const response = await axios.post('/api/honeybee/Create', { FulfillmentId: order.fulfilment_id, Order_ID: order.order_id });

            if (response.status === 201) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: `Order ${order.order_id} sent to Honeybee successfully`,
                    life: 3000
                });
                
                // Refresh the data to get updated status
                loadMailOrders();
            } else {
                // Handle unexpected success status codes
                toast.current.show({
                    severity: 'warn',
                    summary: 'Warning',
                    detail: `Order sent with unexpected status: ${response.status}`,
                    life: 3000
                });
                
                // Still refresh the data
                loadMailOrders();
            }
        } catch (error) {
            let errorMessage = 'Failed to send order to Honeybee';
            
            if (error.response?.data) {
                // Handle different error response formats
                if (typeof error.response.data === 'string') {
                    errorMessage = error.response.data;
                } else if (error.response.data.error) {
                    errorMessage = error.response.data.error;
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
                    // Handle array of errors from Honeybee API
                    errorMessage = error.response.data.errors.map(err => err.message || err.error_detail?.map(d => d.message)?.join(', ') || 'Unknown error').join('; ');
                }
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: errorMessage,
                life: 3000
            });
        } finally {
            // Clear loading state for this specific row
            setData(prevData => prevData.map(item => 
                item.order_id === order.order_id 
                    ? { ...item, sending: false }
                    : item
            ));
        }
    };

    const handleEdit = (order) => {
        setEditingOrder(order);
        setEditForm({
            current_status: order.current_status || '',
            tracking_id: order.tracking_id || '',
            delivery_date: order.delivery_date ? new Date(order.delivery_date) : null
        });
        setEditDialog(true);
    };

    const handleSaveEdit = async () => {
        try {
            // Note: This is just UI for now - no actual API endpoint implemented
            toast.current.show({
                severity: 'info',
                summary: 'Info',
                detail: 'Edit functionality is not yet implemented for Honeybee orders',
                life: 3000
            });
            setEditDialog(false);
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update order',
                life: 3000
            });
        }
    };

    const handleCreateResendRefill = async (orderId) => {
        try {
            if (orderId) {
                setData(prevData => prevData.map(item =>
                    item.order_id === orderId
                        ? { ...item, resending: true }
                        : item
                ));
            }

            if (user.token !== '' && user.token !== null) {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + user.token;
            }

            const payload = orderId ? { orderId } : {};
            const response = await axios.post('/api/ha/CreateHoneybeeResendRefill', payload);

            if (response.data.status === 'OK') {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response.data.data || 'Resend refill created',
                    life: 6000
                });
                loadMailOrders();
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.data.errMsg || 'Failed to create resend refill',
                    life: 5000
                });
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.errMsg || 'Failed to create resend refill',
                life: 5000
            });
        } finally {
            if (orderId) {
                setData(prevData => prevData.map(item =>
                    item.order_id === orderId
                        ? { ...item, resending: false }
                        : item
                ));
            }
        }
    };

    const handleCreateNextFulfillments = async () => {
        try {
            if (user.token !== '' && user.token !== null) {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + user.token;
            }

            const response = await axios.post('/api/ha/CreateNextHoneybeeFulfillments', {});

            if (response.data.status === 'OK') {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response.data.data || 'Next fulfillments created',
                    life: 8000
                });
                loadMailOrders();
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.data.errMsg || 'Failed to create next fulfillments',
                    life: 5000
                });
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.errMsg || 'Failed to create next fulfillments',
                life: 5000
            });
        }
    };

    const handleCreateRefillProcess = async () => {
        try {
            // Set authorization header
            if (user.token !== '' && user.token !== null) {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + user.token;
            }

            const response = await axios.post('/api/ha/CreateRefillsToHoneyBee', {});

            if (response.data.status === 'OK') {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response.data.data || 'Refill process completed successfully',
                    life: 5000
                });
                // Refresh the data to show updated orders
                loadMailOrders();
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.data.errMsg || 'Failed to create refill process',
                    life: 5000
                });
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.errMsg || 'Failed to create refill process',
                life: 5000
            });
        }
    };

    const handleCreateAuto = async () => {
        try {
            // Set authorization header
            if (user.token !== '' && user.token !== null) {
                axios.defaults.headers.common['Authorization'] = 'Bearer ' + user.token;
            }

            const response = await axios.post('/api/honeybee/CreateAuto', {});

            if (response.data.success) {
                const { message, processedCount, successCount, errorCount, errors } = response.data;
                
                let detailMessage = `${message}`;
                if (errorCount > 0 && errors && errors.length > 0) {
                    detailMessage += `\n\nErrors:\n${errors.slice(0, 5).join('\n')}`;
                    if (errors.length > 5) {
                        detailMessage += `\n... and ${errors.length - 5} more errors`;
                    }
                }

                toast.current.show({
                    severity: successCount > 0 ? 'success' : 'warn',
                    summary: 'Auto Process Complete',
                    detail: detailMessage,
                    life: 8000
                });
                
                // Refresh the data to show updated orders
                loadMailOrders();
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.data.error || 'Failed to process orders automatically',
                    life: 5000
                });
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.error || 'Failed to process orders automatically',
                life: 5000
            });
        }
    };

    const renderHeader = () => {
        const monthNames = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        
        return (
            <div className="flex justify-content-between align-items-center">
                <div>
                    <h3 className="m-0">Honeybee Mail Orders - {tabConfigs[activeTab].label}</h3>
                    <p className="mt-1 text-sm text-gray-500">Total Records: {data.length}</p>
                </div>
                <div className="flex align-items-center gap-2">
                    <Button 
                        label="Retry Formulary Failures"
                        icon="pi pi-replay" 
                        onClick={() => handleCreateResendRefill(null)}
                        className="p-button-warning"
                        tooltip="Clear old Rx IDs on 42260 failures and create a refill for the doctor"
                    />
                    <Button 
                        label="Create Next Fulfillments"
                        icon="pi pi-plus" 
                        onClick={handleCreateNextFulfillments}
                        className="p-button-help"
                        tooltip="When last sent fill + 3 months is within 14 days (or past) and no later fills exist: add next 4; queue doctor refill if Honeybee has no refills left"
                    />
                    <Button 
                        label="Create Refill Process"
                        icon="pi pi-refresh" 
                        onClick={handleCreateRefillProcess}
                        className="p-button-success"
                        tooltip="Create refills for orders due in the next 5 days"
                    />
                    <Button 
                        label="Auto Process Orders"
                        icon="pi pi-send" 
                        onClick={handleCreateAuto}
                        className="p-button-primary"
                        tooltip="Automatically process all eligible orders to Honeybee"
                    />
                    <Button 
                        icon="pi pi-chevron-left" 
                        onClick={() => handleMonthNavigation('backward')}
                        className="p-button-outlined"
                    />
                    <div className="text-center px-3">
                        <h4 className="m-0">{monthNames[currentMonth]} {currentYear}</h4>
                    </div>
                    <Button 
                        icon="pi pi-chevron-right" 
                        onClick={() => handleMonthNavigation('forward')}
                        className="p-button-outlined"
                    />
                </div>
            </div>
        );
    };

    const renderSearchBar = () => {
        return (
            <div className="mb-3">
                <div className="flex flex-row gap-2 align-items-end">
                    <div className="flex-1">
                        <label htmlFor="orderId" className="block mb-1">Order ID</label>
                        <InputText
                            id="orderId"
                            value={filters.orderId}
                            onChange={(e) => handleFilterChange('orderId', e.target.value)}
                            placeholder="Search by Order ID"
                            className="p-inputtext-sm w-full"
                        />
                    </div>
                    <div className="flex-1">
                        <label htmlFor="caseId" className="block mb-1">Case ID</label>
                        <InputText
                            id="caseId"
                            value={filters.caseId}
                            onChange={(e) => handleFilterChange('caseId', e.target.value)}
                            placeholder="Search by Case ID"
                            className="p-inputtext-sm w-full"
                        />
                    </div>
                    <div className="flex-1">
                        <label htmlFor="email" className="block mb-1">Email</label>
                        <InputText
                            id="email"
                            value={filters.email}
                            onChange={(e) => handleFilterChange('email', e.target.value)}
                            placeholder="Search by Email"
                            className="p-inputtext-sm w-full"
                        />
                    </div>
                    <div className="flex-1">
                        <label htmlFor="firstName" className="block mb-1">First Name</label>
                        <InputText
                            id="firstName"
                            value={filters.firstName}
                            onChange={(e) => handleFilterChange('firstName', e.target.value)}
                            placeholder="Search by First Name"
                            className="p-inputtext-sm w-full"
                        />
                    </div>
                    <div className="flex-1">
                        <label htmlFor="lastName" className="block mb-1">Last Name</label>
                        <InputText
                            id="lastName"
                            value={filters.lastName}
                            onChange={(e) => handleFilterChange('lastName', e.target.value)}
                            placeholder="Search by Last Name"
                            className="p-inputtext-sm w-full"
                        />
                    </div>
                    <div className="flex gap-2 ml-2">
                        <Button 
                            label="Search" 
                            icon="pi pi-search" 
                            onClick={handleSearch}
                            className="p-button-primary p-button-sm"
                        />
                        <Button 
                            label="Clear" 
                            icon="pi pi-times" 
                            onClick={handleClearSearch}
                            className="p-button-outlined p-button-sm"
                        />
                    </div>
                </div>
            </div>
        );
    };

    const header = renderHeader();

    return (
        <div className="mt-2">
            <Toast ref={toast} />
            <Card>
                {renderSearchBar()}
                
                <TabView activeIndex={activeTab} onTabChange={handleTabChange}>
                    {tabConfigs.map((config, index) => (
                        <TabPanel key={index} header={config.label} leftIcon={config.icon}>
                            <DataTable 
                                value={data} 
                                paginator 
                                rows={25} 
                                rowsPerPageOptions={[25, 50, 100]} 
                                dataKey="fulfilment_id"
                                filters={tableFilters}
                                filterDisplay="row"
                                header={header}
                                emptyMessage="No mail orders found."
                                responsiveLayout="scroll"
                                loading={loading}
                                loadingIcon="pi pi-spinner"
                                className="p-datatable-sm"
                                expandedRows={expandedRows}
                                onRowToggle={(e) => setExpandedRows(e.data)}
                                rowExpansionTemplate={expandedRowTemplate}
                            >
                                <Column expander style={{ width: '3rem' }} />
                                <Column field="order_id" header="Order ID" sortable filter filterPlaceholder="Search by Order ID" showFilterMatchModes={false} />
                                <Column field="fulfilment_id" header="Fulfillment ID" sortable filter filterPlaceholder="Search by Fulfillment ID" showFilterMatchModes={false} />
                                <Column field="fulfillment_no" header="Fill #" sortable filter filterPlaceholder="Search by Fill #" showFilterMatchModes={false} />
                                <Column field="case_id" header="Case ID" body={caseIdBodyTemplate} sortable filter filterPlaceholder="Search by Case ID" showFilterMatchModes={false} />
                                <Column field="first_name" header="First Name" sortable filter filterPlaceholder="Search by First Name" showFilterMatchModes={false} />
                                <Column field="last_name" header="Last Name" sortable filter filterPlaceholder="Search by Last Name" showFilterMatchModes={false} />
                                <Column field="email" header="Email" sortable filter filterPlaceholder="Search by Email" showFilterMatchModes={false} />
                                <Column field="honeybee_patient_id" header="Honeybee Patient ID" sortable filter filterPlaceholder="Search by Patient ID" showFilterMatchModes={false} />
                                <Column field="prescription_id" header="Prescription ID" sortable filter filterPlaceholder="Search by Prescription ID" showFilterMatchModes={false} />
                                <Column field="current_status" header="Status" body={statusBodyTemplate} sortable filter filterPlaceholder="Search by Status" showFilterMatchModes={false} />
                                <Column field="tracking_id" header="Tracking ID" sortable filter filterPlaceholder="Search by Tracking ID" showFilterMatchModes={false} />
                                <Column field="delivery_date" header="Delivery Date" body={dateBodyTemplate} sortable />
                                <Column field="created" header="Created" body={createdBodyTemplate} sortable />
                                <Column field="updated" header="Updated" body={updatedBodyTemplate} sortable />
                                <Column body={actionBodyTemplate} header="Actions" style={{ width: '120px' }} />
                            </DataTable>
                        </TabPanel>
                    ))}
                </TabView>
            </Card>
            <Dialog
                visible={editDialog}
                onHide={() => setEditDialog(false)}
                header="Edit Honeybee Order"
                className="p-dialog-sm"
            >
                <div className="p-fluid p-formgrid p-grid">
                    <div className="p-field p-col-12">
                        <label htmlFor="current_status">Status</label>
                        <Dropdown
                            id="current_status"
                            value={editForm.current_status}
                            options={statusOptions}
                            onChange={(e) => setEditForm({ ...editForm, current_status: e.value })}
                            placeholder="Select Status"
                        />
                    </div>
                    <div className="p-field p-col-12">
                        <label htmlFor="tracking_id">Tracking ID</label>
                        <InputText
                            id="tracking_id"
                            value={editForm.tracking_id}
                            onChange={(e) => setEditForm({ ...editForm, tracking_id: e.target.value })}
                        />
                    </div>
                    <div className="p-field p-col-12">
                        <label htmlFor="delivery_date">Delivery Date</label>
                        <Calendar
                            id="delivery_date"
                            value={editForm.delivery_date}
                            onChange={(e) => setEditForm({ ...editForm, delivery_date: e.value })}
                            dateFormat="mm/dd/yy"
                            showIcon
                        />
                    </div>
                </div>
                <div className="p-dialog-footer">
                    <Button
                        label="Save"
                        icon="pi pi-check"
                        onClick={handleSaveEdit}
                        className="p-button-primary"
                    />
                    <Button
                        label="Cancel"
                        icon="pi pi-times"
                        onClick={() => setEditDialog(false)}
                        className="p-button-outlined"
                    />
                </div>
            </Dialog>
        </div>
    );
}

export default MailOrderHoneybee; 